/**
 * The Editing pipeline (Traci's reference, 21 Oct 2026: editing_pipeline_overdue_today_upcoming_sort.html): one row per
 * booking. Built on the Editing board's queues (board.js), so every rule there (deadlines, two-shooter groups, Proceed,
 * RAWs and Sneak Peek dates, the GoHighLevel cutoff) carries over unchanged. Pure.
 *
 * Traci, 22 Oct 2026 (after trying it):
 *   - Each media type moves on its own; its status works like a tag, so a booking shows under every status it has
 *     (e.g. Photo "Approved & In Progress" and Video "Waiting on Shooter"). No cross-media hold.
 *   - "Waiting on Shooter" (not "second shooter"): whichever shooter on that media is missing. Once one submission for
 *     the media is in, the drawer offers "Proceed Without".
 *   - The row shows the media with the nearest delivery date (photo on a Photo + Video booking until the photos are
 *     delivered, then video), and rows sort by that item: overdue, then due today, then nearest upcoming.
 *   - The drawer always shows every deliverable on the booking, even ones with nothing to do right now.
 *
 * Items a booking can owe:
 *   photo / video   from the board's Initial Review, Issues, Approved & In Progress and Ready to Deliver queues, or
 *                   "Waiting on Photos / Video" when the event has passed and nothing has been uploaded yet (upload due
 *                   3 days after the event; no review clock until it arrives)
 *   sneak           a Sneak Peek to deliver
 *   raws            RAWs to deliver, or a 1-hour Video Add-On's unedited footage (also listed under Video: Traci,
 *                   28 Oct 2026, every booking with video belongs on the Video tab)
 *
 * Traci, 28 Oct 2026: an edited video goes to the outside video editor once approved and is due back two weeks after
 * the wedding (EDITOR_RETURN_DAYS). It keeps "Approved & In Progress" and is overdue past that date. The 1-hour Video
 * Add-On (unedited footage) does not go to the editor. A booking marked complete is no longer waiting on media, and
 * Admin can clear a booking's row by hand (cleared).
 */
import { talentRoles, deliverableFor, QUEUE_ORDER, videoKind, withoutMediaKeys } from './board.js'
import { deadlineFor } from './deadlines.js'
import { normalizePackageType } from '../constants/packagePricing.js'

export const UPLOAD_DUE_DAYS = 3
export const REVIEW_HOURS = 48
export const EDITOR_RETURN_DAYS = 14
export const KIND_LABEL = { photo: 'Photo', video: 'Video', sneak: 'Sneak Peeks', raws: 'RAWs' }
const KIND_ORDER = { photo: 0, video: 1, sneak: 2, raws: 3 }
const ROLE_LABEL = { photographer: 'Photographer', second_photographer: 'Second Photographer', videographer: 'Videographer', second_videographer: 'Second Videographer' }
const PACKAGE_LABEL = { photoOnly: 'Photo Package', photoVideo: 'Photo + Video Package', videoOnly: 'Video Package' }

/** Status filter values (the page's "All Statuses" menu), in the order media moves through them (Traci's review,
 * 22 Oct 2026). A booking matches if any of its media has the status. */
export const STATUSES = {
  waiting: 'Waiting on Media',
  review: 'Needs Review',
  issue: 'Issues',
  waiting_group: 'Waiting on Shooter',
  progress: 'Approved & In Progress',
  waiting_delivery: 'Waits for Media Delivery',
  delivery: 'Ready to Deliver'
}

const dateOnly = v => (v ? String(v).slice(0, 10) : null)
const addDays = (ymd, n) => {
  if (!ymd) return null
  const d = new Date(`${ymd}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}
const mediaOf = row => (row?.media_type === 'Video' ? 'video' : 'photo')
const cancelled = b => String(b?.booking_status || '').toLowerCase().startsWith('cancel')
const delivered = (b, media) => (media === 'video' ? Boolean(b?.video_delivered_at) || b?.video_review_status === 'delivered' : Boolean(b?.photo_delivered_at || b?.media_delivered_at) || b?.photo_review_status === 'delivered')
const complete = b => b?.booking_status === 'completed' || b?.lifecycle_stage === 'booking_complete'
const reviewOf = (b, media) => (media === 'video' ? b?.video_review_status : b?.photo_review_status || b?.review_status) || null

/** overdue | today | upcoming | none */
export function riskOf(due, today) {
  if (!due) return 'none'
  return due < today ? 'overdue' : due === today ? 'today' : 'upcoming'
}
const RISK_RANK = { overdue: 0, today: 1, upcoming: 2, none: 3 }

/** Most urgent first: overdue, due today, nearest upcoming, no date; then photo, video, Sneak Peeks, RAWs. */
export function byUrgency(a, b) {
  return RISK_RANK[a.risk] - RISK_RANK[b.risk] || String(a.due || '9999').localeCompare(String(b.due || '9999')) || KIND_ORDER[a.kind] - KIND_ORDER[b.kind]
}

/** Nearest client delivery date first (the media a row shows); then photo, video, Sneak Peeks, RAWs. */
const byDelivery = (a, b) => String(a.delivery_due || '9999').localeCompare(String(b.delivery_due || '9999')) || KIND_ORDER[a.kind] - KIND_ORDER[b.kind]

/** One photo or video item from the board rows for that media on one booking (the most pressing state wins). */
function mediaItem(kind, rows, b = {}) {
  const at = q => rows.filter(r => r.queue === q)
  const issue = at('issues')[0]
  const review = at('initial_review')
  const progress = at('approved')[0]
  const ready = at('ready_to_deliver')[0]
  const delivery_due = (issue || review[0] || progress || ready)?.deadline || null
  // A shooter on this media has sent nothing yet (board.js mediaGroup, on any of the rows)
  const group = rows.find(r => r.group)?.group || null
  const missing = group?.still_waiting?.length ? group.still_waiting : []
  const extra = missing.length ? { waiting_on: missing, extra_tags: ['waiting_group'], proceed_ready: group.received >= 1 } : {}
  if (issue) return { kind, status: 'issue', label: 'Issue', detail: issue.issues || 'An issue is recorded', due: issue.deadline || null, delivery_due, ...extra }
  if (review.length) {
    const first = review.map(r => dateOnly(r.uploaded_at)).filter(Boolean).sort()[0] || null
    // Initial Review: due 48 hours from the upload (board.js rowAlert)
    const due = first ? addDays(first, REVIEW_HOURS / 24) : null
    const who = review.length > 1 ? `${review.length} submissions` : review[0].talent_name || review[0].role_label
    return { kind, status: 'review', label: 'Needs Review', detail: `${who} · received ${first || '—'}`, due, review_due: true, delivery_due, ...extra }
  }
  if (progress?.waiting) return { kind, status: 'waiting_group', label: 'Waiting on Shooter', detail: missing.length ? `Waiting on ${missing.join(', ')}` : `${progress.group.approved} of ${progress.group.expected} approved`, due: progress.deadline || null, delivery_due, ...extra, extra_tags: [] }
  if (progress && kind === 'video' && videoKind(b).kind !== 'addon_footage' && dateOnly(b.event_date)) {
    // With the outside video editor: due back two weeks after the wedding
    const back = addDays(dateOnly(b.event_date), EDITOR_RETURN_DAYS)
    return { kind, status: 'progress', label: 'Approved & In Progress', detail: `With the video editor · due back ${back}`, due: back, due_label: 'Editor return by', editor_due: back, delivery_due, ...extra }
  }
  if (progress) return { kind, status: 'progress', label: 'Approved & In Progress', detail: progress.deliverable ? `Deliverable ${progress.deliverable}` : 'Being edited', due: progress.deadline || null, delivery_due, ...extra }
  if (ready) return { kind, status: 'delivery', label: 'Ready to Deliver', detail: 'Editing finished', due: ready.deadline || null, delivery_due, ...extra }
  return null
}

/**
 * @param p.queues    buildEditingBoard().queues (each row: booking_id, media_type, deadline, uploaded_at, ...)
 * @param p.bookings  booking rows (as loaded for the board)
 * @param p.forms     job_completion_forms rows (booking_id, talent_role, submitted_at, review_status, reviewed_at)
 * @param p.names     Map id -> display name (clients and talent)
 * @param p.couples   Map booking id -> couple names
 * @param p.today     YYYY-MM-DD
 * @param p.cutoff    YYYY-MM-DD; events on or before it were edited in GoHighLevel (no Waiting on Media for them)
 * @param p.cleared   booking ids Admin took off the pipeline by hand
 * @param p.jobs      Map booking_id:media -> editing_jobs row (who the job went ahead without)
 */
export function buildPipeline({ queues = {}, bookings = [], forms = [], names = new Map(), couples = new Map(), today, cutoff = null, cleared = new Set(), jobs = new Map(), received = new Map() }) {
  const byBooking = new Map()
  const bookingById = new Map(bookings.map(b => [b.id, b]))
  const bucket = id => {
    if (!byBooking.has(id)) byBooking.set(id, { photo: [], video: [], sneak: [], raws: [] })
    return byBooking.get(id)
  }
  for (const q of QUEUE_ORDER) {
    for (const r of queues[q] || []) {
      const row = { ...r, queue: q }
      if (q === 'sneak_peeks') bucket(r.booking_id).sneak.push(row)
      else if (q === 'raws') bucket(r.booking_id).raws.push(row)
      else bucket(r.booking_id)[mediaOf(r)].push(row)
    }
  }
  const formsBy = new Map()
  for (const f of forms) {
    const key = `${f.booking_id}:${f.talent_role || 'photographer'}`
    if (!formsBy.has(key) || String(f.submitted_at || '') > String(formsBy.get(key).submitted_at || '')) formsBy.set(key, f)
  }
  const without = withoutMediaKeys(jobs, forms)

  // Waiting on Photos / Video: the event has passed, talent is on the media, nothing uploaded and nothing on the board
  for (const b of bookings) {
    if (cancelled(b) || b.is_test || complete(b)) continue
    const event = dateOnly(b.event_date)
    if (!event || event >= today || (cutoff && event <= cutoff)) continue
    for (const media of ['photo', 'video']) {
      const roles = talentRoles(b).filter(t => t.media === media)
      if (!roles.length || delivered(b, media)) continue
      if (media === 'video' && videoKind(b).kind === 'addon_footage') continue // its footage sits with the RAWs
      const onBoard = byBooking.get(b.id)?.[media]?.length
      if (onBoard) continue
      if (roles.some(t => formsBy.get(`${b.id}:${t.role}`)?.submitted_at)) continue
      bucket(b.id)[media].push({ queue: 'waiting', booking_id: b.id, media_type: media === 'video' ? 'Video' : 'Photo', upload_due: addDays(event, UPLOAD_DUE_DAYS) })
    }
  }

  const rows = []
  for (const [bookingId, parts] of byBooking) {
    if (cleared.has(bookingId)) continue
    const b = bookingById.get(bookingId) || {}
    const any = parts.photo[0] || parts.video[0] || parts.sneak[0] || parts.raws[0] || {}
    const items = []
    for (const kind of ['photo', 'video']) {
      const list = parts[kind]
      if (!list.length) continue
      const waiting = list.find(r => r.queue === 'waiting')
      const item = waiting
        ? { kind, status: 'waiting', label: `Waiting on ${kind === 'video' ? 'Video' : 'Photos'}`, detail: `Upload due ${waiting.upload_due} · no review clock yet`, due: waiting.upload_due, delivery_due: deadlineFor(b, kind).deadline }
        : mediaItem(kind, list, b)
      if (item) items.push({ ...item, rows: list.filter(r => r.queue !== 'waiting') })
    }
    for (const r of parts.sneak) items.push({ kind: 'sneak', status: 'delivery', label: 'Ready to Deliver', detail: '10 edited highlights', due: r.deadline || null, delivery_due: r.deadline || null, rows: [r] })
    for (const r of parts.raws) {
      const footage = r.deliver_scope === 'footage'
      const ready = Boolean(r.deliver_ready)
      items.push({
        kind: 'raws', status: ready ? 'delivery' : 'waiting_delivery', footage,
        label: ready ? 'Ready to Deliver' : footage ? 'Waiting on Upload' : 'Waits for Media Delivery',
        detail: footage ? 'Video Add-On footage (unedited)' : r.raw_scope || 'RAWs', due: r.deadline || null, delivery_due: r.deadline || null, rows: [r]
      })
    }
    if (!items.length) continue
    for (const it of items) it.risk = riskOf(it.due, today)
    // The row shows the media with the nearest delivery date; the drawer lists them all
    const byDue = [...items].sort(byDelivery)
    items.sort((a, c) => KIND_ORDER[a.kind] - KIND_ORDER[c.kind])
    const type = normalizePackageType(b.package_type)
    rows.push({
      booking_id: bookingId,
      ref: String(bookingId).slice(0, 8).toUpperCase(),
      client_id: b.customer_id || any.client_id || null,
      client_name: names.get(b.customer_id) || any.client_name || null,
      couple: couples.get(bookingId) || any.couple || null,
      event_date: dateOnly(b.event_date) || any.event_date || null,
      package_label: b.personalization_data?.package?.packageTitle || PACKAGE_LABEL[type] || 'Package',
      hours: Number(b.hours_booked) || null,
      rush: items.some(i => i.rows?.some(r => r.tier === 'rush')),
      next: byDue[0],
      after: byDue[1] || null,
      items,
      sections: sectionsFor(b, items, today, cutoff),
      tags: [...new Set(items.flatMap(i => [i.kind, i.status, ...(i.footage ? ['video'] : []), ...(i.extra_tags || [])]))],
      talent: talentDetail(b, formsBy, names, without, received)
    })
  }
  rows.sort((a, b) => byUrgency(a.next, b.next) || String(a.event_date || '').localeCompare(String(b.event_date || '')))
  const anyRisk = (r, risk) => r.items.some(i => i.risk === risk)
  const kpis = {
    active: rows.length,
    review: rows.filter(r => r.tags.includes('review')).length,
    waiting: rows.filter(r => r.tags.includes('waiting')).length,
    today: rows.filter(r => anyRisk(r, 'today')).length,
    overdue: rows.filter(r => anyRisk(r, 'overdue')).length
  }
  return { rows, kpis }
}

/**
 * Everything the booking delivers, for the drawer (Traci, 22 Oct 2026: "The drawer should always show the full booking
 * with all deliverables sections"): each media on the booking, open or not, then its Sneak Peeks and RAWs.
 */
function sectionsFor(b, items, today, cutoff) {
  const out = []
  const event = dateOnly(b.event_date)
  for (const media of ['photo', 'video']) {
    if (!talentRoles(b).some(t => t.media === media)) continue
    const open = items.find(i => i.kind === media)
    if (open) { out.push(open); continue }
    const status = reviewOf(b, media)
    const doneAt = dateOnly(media === 'video' ? b.video_delivered_at : b.photo_delivered_at || b.media_delivered_at)
    const label = delivered(b, media) ? 'Delivered'
      : event && event > today ? 'Event not yet'
        : cutoff && event && event <= cutoff ? 'Edited in GoHighLevel'
          : status === 'approved' ? 'Approved & In Progress' : status === 'ready_for_delivery' ? 'Ready to Deliver' : 'Nothing to do yet'
    out.push({ kind: media, status: delivered(b, media) ? 'done' : 'idle', label, detail: doneAt ? `Delivered ${doneAt}` : null, due: null, delivery_due: deadlineFor(b, media).deadline, risk: 'none', rows: [] })
  }
  for (const i of items) if (i.kind === 'sneak' || i.kind === 'raws') out.push(i)
  if (b.raws_delivered_at && !items.some(i => i.kind === 'raws')) out.push({ kind: 'raws', status: 'done', label: 'Delivered', detail: `Delivered ${dateOnly(b.raws_delivered_at)}`, due: null, risk: 'none', rows: [] })
  return out
}

/**
 * The Open Booking drawer's talent table: who is on each media, what they should upload, when it came in and was
 * approved, and the delivery date it feeds. With two shooters on a media each talent has their own approval
 * (job_completion_forms.review_status); with one, the media's own status is theirs, so an Issue shows as an Issue.
 */
function talentDetail(b, formsBy, names, without = new Set(), received = new Map()) {
  const hours = Number(b.hours_booked) || 0
  const roles = talentRoles(b)
  return roles.map(t => {
    const f = formsBy.get(`${b.id}:${t.role}`) || null
    const shared = roles.filter(x => x.media === t.media).length > 1
    const media = reviewOf(b, t.media)
    const own = shared && f?.review_status ? f.review_status : null
    // A shared media's state is not a talent's who sent nothing: they are still waited on (or gone without)
    const s = shared && !f?.submitted_at ? null : own || (f?.submitted_at || media ? media : null)
    // Admin went ahead without this talent's media (Traci, 29 Oct 2026)
    // Media marked received by hand, no completion form (Traci's Loom, 28 Sep 2026): Editing can approve it without one
    const marked = !f?.submitted_at ? received.get(`${b.id}:${t.role}`) || null : null
    const base = without.has(`${b.id}:${t.role}`) ? 'Proceeded Without Media'
      : delivered(b, t.media) ? 'Delivered'
      : !f?.submitted_at && !media ? 'Waiting on Media'
        : s === 'has_issues' ? 'Issue'
          : s === 'approved' ? 'Approved'
            : s === 'ready_for_delivery' ? 'Editing Finished'
              : f?.submitted_at ? 'Needs Review' : 'Waiting on Media'
    const status = base === 'Waiting on Media' && marked ? 'Media Received (no form)' : base
    const lead = t.role === 'photographer' || t.role === 'videographer'
    return {
      role: t.role,
      role_label: ROLE_LABEL[t.role],
      talent_id: t.talent_id,
      talent_name: names.get(t.talent_id) || null,
      media: t.media,
      expected: t.media === 'photo' ? `${hours ? `${hours * 100}+ photos (100+ per coverage hour)` : '100+ photos per coverage hour'} · completion form` : 'Video media + completion form',
      deliverable: lead ? deliverableFor(b, t.media) : `Feeds the ${t.media === 'video' ? 'film' : 'gallery'}`,
      received_at: dateOnly(f?.submitted_at) || (status === 'Media Received (no form)' ? dateOnly(marked.at) : null),
      received_by_hand: status === 'Media Received (no form)' ? { by: marked.by, note: marked.note } : null,
      approved_at: status === 'Approved' || status === 'Editing Finished' || status === 'Delivered' ? dateOnly(f?.reviewed_at) : null,
      status,
      media_due: deadlineFor(b, t.media).deadline
    }
  })
}

const MEDIA_TABS = ['photo', 'video', 'sneak', 'raws']

/** The rows a tab, status, search and sort leave (page side; also used by the tests). */
export function filterPipeline(rows, { tab = 'all', status = 'all', q = '', sort = 'priority' } = {}) {
  const term = String(q || '').trim().toLowerCase()
  const out = rows.filter(r => {
    if ((tab === 'today' || tab === 'overdue') && !r.items.some(i => i.risk === tab)) return false
    if (MEDIA_TABS.includes(tab) && !r.tags.includes(tab)) return false
    if (status !== 'all' && !r.tags.includes(status)) return false
    if (term && ![r.client_name, r.couple, r.ref, r.booking_id, ...r.talent.map(t => t.talent_name)].some(v => String(v || '').toLowerCase().includes(term))) return false
    return true
  })
  // True due date: when the media is due to the client, earliest first (Traci, 28 Oct 2026)
  if (sort === 'true_due') out.sort((a, b) => String(shown(a, tab, status).delivery_due || '9999').localeCompare(String(shown(b, tab, status).delivery_due || '9999')))
  else if (sort === 'due') out.sort((a, b) => String(shown(a, tab, status).due || '9999').localeCompare(String(shown(b, tab, status).due || '9999')))
  else if (sort === 'event') out.sort((a, b) => String(a.event_date || '').localeCompare(String(b.event_date || '')))
  else out.sort((a, b) => byUrgency(shown(a, tab, status), shown(b, tab, status)))
  return out
}

/**
 * The item a row shows: on a media tab, that media's item; on Due Today / Overdue, the item that is; filtered to a
 * status, the item with it; otherwise the media with the nearest delivery date.
 */
export function shown(row, tab = 'all', status = 'all') {
  if (tab === 'video') return row.items.find(i => i.kind === 'video') || row.items.find(i => i.footage) || row.next
  if (MEDIA_TABS.includes(tab)) return row.items.find(i => i.kind === tab) || row.next
  if (tab === 'today' || tab === 'overdue') return [...row.items].filter(i => i.risk === tab).sort(byUrgency)[0] || row.next
  if (status !== 'all') return row.items.find(i => i.status === status || (i.extra_tags || []).includes(status)) || row.next
  return row.next
}
